import { useCallback, useEffect, useMemo, useState } from 'react';

import { Table } from 'antd';
import { useShallow } from 'zustand/shallow';

import { useConditionalTableScroll } from '@/shared/hooks/useConditionalTableScroll';
import { EMPTY_MESSAGES, PAGINATION_OPTIONS } from '@/shared/lib/constants';
import { Button } from '@/shared/ui/Button';
import { Icon } from '@/shared/ui/Icon';
import { TableEmpty } from '@/shared/ui/TableEmpty';

import { useDagDashboardList } from '@/entities/dag';

import { buildDagRowTriggerRequest } from '@/features/dag-list/lib/buildDagRowTriggerRequest';
import { getDagTableColumns } from '@/features/dag-list/lib/getDagTableColumns';
import { getDagTagOptions } from '@/features/dag-list/lib/getDagTagOptions';
import {
  buildDagTableRows,
  filterDagTableRows,
  paginateDagTableRows,
  sortDagTableRows,
} from '@/features/dag-list/model/dagListDashboardSelectors';
import { type DagTableRow } from '@/features/dag-list/model/types';
import { DAG_LIST_FRONTEND_LIMIT, useDagListDashboardStore } from '@/features/dag-list/model/useDagListDashboardStore';
import { useDagListFilterState } from '@/features/dag-list/model/useDagListFilters';
import { useDagTablePagination } from '@/features/dag-list/model/useDagTable';
import { useDeleteDagMutation } from '@/features/dag-list/model/useDeleteDagMutation';
import { useToggleDagBookmarkMutation } from '@/features/dag-list/model/useToggleDagBookmarkMutation';
import { useToggleDagSchedulingMutation } from '@/features/dag-list/model/useToggleDagSchedulingMutation';
import { useTriggerDagMutation } from '@/features/dag-list/model/useTriggerDagMutation';
import { CreateDagModal } from '@/features/dag-list/ui/CreateDagModal/CreateDagModal';
import { DagListFilters } from '@/features/dag-list/ui/DagListFilters/DagListFilters';
import { DeleteDagModal } from '@/features/dag-list/ui/DeleteDagModal';

import styles from './DagTableContent.module.css';

export const DagTableContent = () => {
  const {
    filters,
    debouncedSearchQuery,
    setSearchQuery,
    setSelectedRunStatus,
    setSelectedFavorite,
    setSelectedMaturity,
    setSelectedTags,
  } = useDagListFilterState();

  const { currentPage, currentPageSize, sortBy, sortOrder, handleTableChange, handlePaginationChange } =
    useDagTablePagination();

  const {
    sourceItems,
    selectedVersionByDagUuid,
    setSelectedVersion,
    isLoading,
    setSourceItems,
    setIsLoading,
    setError,
    markPreserveOnUnmount,
    clearPreserveOnUnmount,
    resetState,
  } = useDagListDashboardStore(
    useShallow((state) => ({
      sourceItems: state.sourceItems,
      selectedVersionByDagUuid: state.selectedVersionByDagUuid,
      setSelectedVersion: state.setSelectedVersion,
      isLoading: state.isLoading,
      error: state.error,
      setSourceItems: state.setSourceItems,
      setIsLoading: state.setIsLoading,
      setError: state.setError,
      markPreserveOnUnmount: state.markPreserveOnUnmount,
      clearPreserveOnUnmount: state.clearPreserveOnUnmount,
      resetState: state.resetState,
    }))
  );

  const [pendingDeleteDag, setPendingDeleteDag] = useState<{ dagUuid: string; name?: string } | null>(null);
  const [isCreateDagModalOpen, setIsCreateDagModalOpen] = useState(false);

  const dagListQuery = useDagDashboardList({
    offset: 0,
    limit: DAG_LIST_FRONTEND_LIMIT,
  });

  const { mutateAsync: deleteDag, isPending: isDeletingDag, variables: deletingDagUuid } = useDeleteDagMutation();
  const {
    mutate: toggleDagBookmark,
    isPending: isBookmarkingDag,
    variables: bookmarkingDagUuid,
  } = useToggleDagBookmarkMutation();
  const { mutate: triggerDag, isPending: isTriggeringDag, variables: triggeringDagUuid } = useTriggerDagMutation();
  const {
    mutate: toggleDagScheduling,
    isPending: isSchedulingDag,
    variables: schedulingDagUuid,
  } = useToggleDagSchedulingMutation();

  const tableRows = useMemo(
    () => buildDagTableRows(sourceItems, selectedVersionByDagUuid),
    [selectedVersionByDagUuid, sourceItems]
  );
  const debouncedFilters = useMemo(
    () => ({
      ...filters,
      searchQuery: debouncedSearchQuery,
    }),
    [debouncedSearchQuery, filters]
  );
  const filteredRows = useMemo(() => filterDagTableRows(tableRows, debouncedFilters), [debouncedFilters, tableRows]);
  const sortedRows = useMemo(
    () => sortDagTableRows(filteredRows, sortBy, sortOrder),
    [filteredRows, sortBy, sortOrder]
  );
  const visibleRows = useMemo(
    () => paginateDagTableRows(sortedRows, currentPage, currentPageSize),
    [currentPage, currentPageSize, sortedRows]
  );
  const dagTagOptions = useMemo(() => getDagTagOptions(sourceItems.map((item) => item.tags ?? [])), [sourceItems]);

  const paginationConfig = {
    current: currentPage,
    pageSize: currentPageSize,
    total: sortedRows.length,
    pageSizeOptions: [...PAGINATION_OPTIONS],
    showSizeChanger: true,
    locale: { items_per_page: '' },
    showTotal: (total: number, range: [number, number]) => `(${total}개 중 ${range[0]}-${range[1]})`,
    onChange: handlePaginationChange,
  };

  const scrollConfig = useConditionalTableScroll({
    dataLength: Math.min(visibleRows.length, currentPageSize),
    pageSize: currentPageSize,
    viewportOffset: 420,
    rowHeight: 40,
    headerHeight: 40,
    paginationHeight: 64,
  });

  const handleActivationClick = useCallback(
    (dagUuid: string, enabled: boolean) => {
      toggleDagScheduling({
        dagUuid,
        enabled,
      });
    },
    [toggleDagScheduling]
  );

  const handleVersionChange = useCallback(
    (dagUuid: string, versionLabel: string) => {
      setSelectedVersion(dagUuid, Number(versionLabel));
    },
    [setSelectedVersion]
  );

  const handleDeleteConfirm = useCallback((dagUuid: string, name?: string) => {
    setPendingDeleteDag({ dagUuid, name });
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setPendingDeleteDag(null);
  }, []);

  const handleDeleteDag = useCallback(async () => {
    if (!pendingDeleteDag) return;

    await deleteDag({ dagUuid: pendingDeleteDag.dagUuid });
    setPendingDeleteDag(null);
  }, [deleteDag, pendingDeleteDag]);

  const handleTriggerClick = useCallback(
    (record: DagTableRow) => {
      const request = buildDagRowTriggerRequest(record);

      if (request) {
        triggerDag(request);
      }
    },
    [triggerDag]
  );

  const handleFavoriteToggle = useCallback(
    (dagUuid: string) => {
      toggleDagBookmark({ dagUuid });
    },
    [toggleDagBookmark]
  );

  const handleDagDetailNavigate = useCallback(() => {
    markPreserveOnUnmount();
  }, [markPreserveOnUnmount]);

  const activationLoadingDagUuid = isSchedulingDag ? schedulingDagUuid?.dagUuid : undefined;
  const currentDeletingDagUuid = isDeletingDag ? deletingDagUuid?.dagUuid : undefined;
  const currentTriggeringDagUuid = isTriggeringDag ? triggeringDagUuid?.dagUuid : undefined;
  const currentBookmarkingDagUuid = isBookmarkingDag ? bookmarkingDagUuid?.dagUuid : undefined;

  const columns = useMemo(
    () =>
      getDagTableColumns({
        activationLoadingDagUuid,
        deletingDagUuid: currentDeletingDagUuid,
        triggeringDagUuid: currentTriggeringDagUuid,
        bookmarkingDagUuid: currentBookmarkingDagUuid,
        onDagDetailNavigate: handleDagDetailNavigate,
        onFavoriteToggle: handleFavoriteToggle,
        onActivationClick: handleActivationClick,
        onTriggerClick: handleTriggerClick,
        onDeleteClick: handleDeleteConfirm,
        onVersionChange: handleVersionChange,
        sortBy,
        sortOrder,
        searchQuery: debouncedSearchQuery,
      }),
    [
      activationLoadingDagUuid,
      currentBookmarkingDagUuid,
      currentDeletingDagUuid,
      currentTriggeringDagUuid,
      handleActivationClick,
      handleDagDetailNavigate,
      handleDeleteConfirm,
      handleFavoriteToggle,
      handleTriggerClick,
      handleVersionChange,
      debouncedSearchQuery,
      sortBy,
      sortOrder,
    ]
  );

  const onCreateDagClick = () => {
    setIsCreateDagModalOpen(true);
  };

  const handleCloseCreateDagModal = () => {
    setIsCreateDagModalOpen(false);
  };

  useEffect(() => {
    setSourceItems(dagListQuery.data?.items ?? []);
    setIsLoading(dagListQuery.isLoading || dagListQuery.isFetching);
    setError(dagListQuery.isError ? dagListQuery.error : null);
  }, [
    dagListQuery.data?.items,
    dagListQuery.error,
    dagListQuery.isError,
    dagListQuery.isFetching,
    dagListQuery.isLoading,
    setError,
    setIsLoading,
    setSourceItems,
  ]);

  useEffect(() => {
    return () => {
      if (useDagListDashboardStore.getState().preserveOnUnmount) {
        clearPreserveOnUnmount();
        return;
      }

      resetState();
    };
  }, [clearPreserveOnUnmount, resetState]);

  return (
    <>
      <div className={styles.addButtonContainer}>
        <h2 className={styles.title}>DAG</h2>
        <Button
          className={styles.addButton}
          variant="filled"
          prefix={<Icon name="plus-outlined" />}
          onClick={onCreateDagClick}>
          DAG 추가
        </Button>
      </div>

      <CreateDagModal open={isCreateDagModalOpen} onClose={handleCloseCreateDagModal} />

      <DeleteDagModal
        open={Boolean(pendingDeleteDag)}
        confirmLoading={isDeletingDag}
        onConfirm={handleDeleteDag}
        onCancel={handleDeleteCancel}
      />

      <DagListFilters
        searchQuery={filters.searchQuery}
        selectedRunStatus={filters.selectedRunStatus}
        selectedFavorite={filters.selectedFavorite}
        selectedMaturity={filters.selectedMaturity}
        selectedTags={filters.selectedTags}
        dagTagOptions={dagTagOptions}
        isDagTagOptionsLoading={isLoading}
        onSearchChange={setSearchQuery}
        onRunStatusFilterChange={setSelectedRunStatus}
        onFavoriteFilterChange={setSelectedFavorite}
        onMaturityFilterChange={setSelectedMaturity}
        onTagFilterChange={setSelectedTags}
      />

      <Table
        className={styles.table}
        columns={columns}
        dataSource={visibleRows}
        rowKey="dagUuid"
        pagination={paginationConfig}
        scroll={{ x: visibleRows.length > 0 ? 'max-content' : undefined, ...scrollConfig }}
        loading={isLoading}
        locale={{ emptyText: <TableEmpty searchQuery={debouncedSearchQuery} description={EMPTY_MESSAGES.NO_DAGS} /> }}
        onChange={handleTableChange}
        sticky={true}
      />
    </>
  );
};
